import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCalcDraft, useFilterCalcDrafts } from "../../../../common/api/queries";
import { QueryKeys, TableSizes } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { hasAllPermissions, hasPermission } from "../../../../common/security/utils";
import { usePermissions } from "../../../auth/hooks";
import { CalcType } from "../../enums";
import { CALC_ROUTE_PATHS } from "../../paths";
import { CalcTypeTag } from "../../records/components/CalcTypeTag";
import { VehicleCalcDraftList } from "../types";
import { CalcDraftAttachmentsTableView } from "./CalcDraftAttachmentsTableView";

export const VehicleCalcDraftTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const accountPermissions = usePermissions();

  const calcTypes = useMemo(() => {
    const types: CalcType[] = [];

    if (hasPermission(accountPermissions, Permission.MTPL_CALCULATE)) {
      types.push(CalcType.MTPL);
    }
    if (hasPermission(accountPermissions, Permission.CRASH_CALCULATE)) {
      types.push(CalcType.CRASH, CalcType.GAP, CalcType.PAS);
    }
    if (hasAllPermissions(accountPermissions, [Permission.MTPL_CALCULATE, Permission.CRASH_CALCULATE])) {
      types.push(CalcType.MTPL_CRASH);
    }

    return types;
  }, [accountPermissions]);

  const searchParam = useFilterSearchParams();

  const { data } = useFilterCalcDrafts({ ...searchParam, calcTypes });
  const deleteCalcDraft = useDeleteCalcDraft();

  const columns: ColumnsType<VehicleCalcDraftList> = [
    {
      key: "calcType",
      title: t("common.type"),
      width: 120,
      render: (_, record) => <CalcTypeTag type={record.calcType} style={{ marginRight: 0 }} />,
    },
    {
      key: "clientName",
      title: t("calc.draft.attrs.clientName"),
      width: 210,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.clientName}</Ellipsis>,
    },
    {
      key: "vehicle",
      title: t("common.vehicle"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Ellipsis>
          {[
            `${record.customBrand || record.brandName} ${record.customModel || record.modelName}`,
            record.licensePlate,
            record.vin,
            t("calc.vehicle.enums.vehiclePurpose." + record.purpose),
          ]
            .filter((t) => !!t)
            .join(" | ")}
        </Ellipsis>
      ),
    },
    {
      key: "attachmentsCount",
      title: t("calc.draft.attrs.attachmentsCount"),
      width: 60,
      align: "right",
      render: (_, record) => record.attachments?.length || 0,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 190,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={`${CALC_ROUTE_PATHS.vehicle.to}${URLQuery.searchParamsToString({ searchParams: { [QueryKeys.DRAFT_ID]: record.id } })}`}
            target="_blank"
            icon="import"
            color="blue"
            text={t("calc.draft.actions.setToCalcForm")}
          />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("calc.draft.titles.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteCalcDraft.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Table<VehicleCalcDraftList>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={data?.pageData as VehicleCalcDraftList[] | undefined}
      expandable={{
        expandedRowRender: (record) => <CalcDraftAttachmentsTableView calcDraft={record} />,
      }}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
